import {
    Box,
    Button,
    Stack,
    SxProps
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { useRef, useMemo, useState, useEffect } from 'react';
import { DataTable } from "@components/data-table";
import { LoadingTableBlock } from "@components/data-table/loading-table-block";
import { TableDesktopFooterMenu } from "@components/data-table/data-table-desktop-footer-menu";
import { DataTableDesktopHeaderMenu } from "@components/data-table/data-table-desktop-header-menu";
import { TableSearchInput } from "@components/data-table/search-input"
import { ScannerPopover } from "@components/data-table/scanner-popover";
import { FilterType } from "@components/data-table/types";
import { TableSettingsMenu } from "@components/data-table/settings-menu";
import { DataTableCellValue, DataTableRowData } from "@components/data-table/types";
import { TableMobileMenu } from "@components/data-table/mobile-menu";
import { TableFilterInput } from "@components/data-table/filter-input";
import { TableEditItemsPopover } from "@views/tickets-table/edit-items-popover";
import { TableDeleteItemsPopover } from "@components/data-table/delete-items-popover";

import { getOptionsFromSelectFieldConfig } from "@hooks/use-config/utils";
import { WpGenericPost, WpTicketMetaBox } from "@hooks/use-get/tickets/types"
import { useGetAssets } from "@hooks/use-get/assets";
import { TicketTableColumnFromApi } from "@hooks/use-config-context/ticket/types";
import { useTicketTableContext, useTicketTableDispatch } from '@hooks/use-table-context/ticket';
import { ID_KEY as TICKET_KEY_ID, PER_PAGE_OPTIONS } from "@hooks/use-table-context/utils";
import { TICKET_KEY_FOR_ASSETS } from "@hooks/use-table-context/ticket/utils";
import { useTicketConfigContext } from '@hooks/use-config-context/ticket';
import { useTableAnchorsState } from "@hooks/use-table-anchors-state";
import { dataTableResponsiveWidth, formatNullCellValue } from "@components/data-table/utils";
import { TicketTableFilter } from "@hooks/use-table-context/ticket/types"
import { formatTicketForSave } from "@utils/format-ticket-for-save";

import { ClosedTicketsFilter } from "../filter-input/closed-tickets-filter";
import { ticketTableCellFormatters } from "../utils";
import { useCapabilities } from "@hooks/use-capabilities";


const formatTicketTableRows = (
    items: WpGenericPost<WpTicketMetaBox>[],
    ticketColumns: TicketTableColumnFromApi[]
) => {
    return items.map((ticket) => {
        const ticketData = ticketColumns.reduce((acc, column) => {
            const value = column.getValue(ticket);
            return { ...acc, [column.id]: value };
        }, {});
        return { ...ticketData, id: ticket.id };
    });
}
export const TicketTableComponent = ({
    sx,
    hideAddTicket = false,
    openTicketModal
} : {
    sx?: SxProps;
    hideAddTicket?: Boolean;
    openTicketModal?: (data?: any) => void;
})  => {
    const mobileAnchorRef = useRef<HTMLDivElement>(null);

    const { 
        tableData,
        loading,
        hiddenColumns,
        disabledRows,
        page,
        perPage,
        total,
        selectedItems,
        showClosed,
        filters
     } = useTicketTableContext();

     const {
        selectItem,
        editItems,
        deleteItems,
        addFilter,
        removeFilter,
        removeAllFilters,
        setPage,
        setPerPage,
        addHiddenColumn,
        removeHiddenColumn,
     } = useTicketTableDispatch();

    const {
        searchMenuAnchor,
        setSearchMenuAnchor,
        filterMenuAnchor,
        setFilterMenuAnchor,
        editTicketsAnchor,
        setEditTicketsAnchor,
        deleteItemsAnchor,
        setDeleteItemsAnchor,
        settingsAnchor,
        setSettingsAnchor,
    } = useTableAnchorsState();

    const [filtersToRemove, setFiltersToRemove] = useState<TicketTableFilter[]>([]);


    const { can } = useCapabilities();
    const canCreate = can("create_tickets");
    const canEdit = can("edit_tickets");
    const canDelete = can("delete_tickets");
    const { getOneAssetByAssetTag } = useGetAssets();

    const [shouldShowDesktopFooter, setShouldShowDesktopFooter] =
    useState<boolean>(window.innerWidth >= 900);

    const [showScanner, setShowScanner] = useState<boolean>(false);

        const setSearchMenuAnchorMobile = () =>
        mobileAnchorRef.current && setSearchMenuAnchor(mobileAnchorRef.current);
    const setFilterMenuAnchorMobile = () =>
        mobileAnchorRef.current && setFilterMenuAnchor(mobileAnchorRef.current);

    const { tableColumns, config: ticketsConfig } = useTicketConfigContext();
    const tableStackRef = useRef<HTMLDivElement>(null);

    const activeTableColumns = useMemo(
        () => 
            tableColumns?.filter(
                (column) => !hiddenColumns.includes(column.id)
            ),
        [tableColumns, hiddenColumns]
    )

    const activeTableLabels = useMemo(
        () => 
            tableColumns.reduce(
                    (acc, column) => ({
                        ...acc,
                        [column.id]: column.label,
                    }),
                    {} as Record<string, string>
                ),
            [tableColumns]
    );

    const ticketTableIds = useMemo(
        () => activeTableColumns.map((col) => col.id),
        [activeTableColumns]
    );

        useEffect(() => {
            const handleResize = () => {
                const isDesktop = window.innerWidth >= 900;
                if (shouldShowDesktopFooter !== isDesktop)
                    setShouldShowDesktopFooter(isDesktop);
            };
            window.addEventListener("resize", handleResize);
            handleResize();
            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }, [shouldShowDesktopFooter]);

    const formatters = useMemo(() => {
        const baseFormatters: any = ticketTableCellFormatters(
            activeTableColumns,
            ticketsConfig
        );
        
        // Find the column that should be the link. 
        // We'll try 'title' first, then 'ticket_tag', then the first column.
        const linkColumnId = activeTableColumns.find(col => col.id === 'title' )?.id || activeTableColumns[0]?.id;

        if (linkColumnId) {
            baseFormatters[linkColumnId] = (val: DataTableCellValue, rowData: DataTableRowData) => (
                <span
                    className={"link"}
                    onClick={() => openTicketModal && openTicketModal(rowData)}
                >
                    {formatNullCellValue(val)}
                </span>
            );
        }

        return baseFormatters;
    }, [activeTableColumns, ticketsConfig, openTicketModal]);

    // Filter tableData based on showClosedTickets flag
    const filteredTableData = useMemo(() => {
        if (showClosed) {
            return tableData;
        }
        // Filter out tickets where status_ticket === "closed"
        return tableData.filter(ticket => {
            const status = ticket.meta_box?.status_ticket;
            return status !== "Closed";
        });
    }, [tableData, showClosed]);

    return (
        <Box sx={{ width: '100%', position: 'relative', ...sx }}>
            <Stack className="ticket-table-stack table-stack" ref={tableStackRef}>

            <DataTableDesktopHeaderMenu
                setSearchAnchor={setSearchMenuAnchor}
                setFilterAnchor={setFilterMenuAnchor}
                setSettingsAnchor={setSettingsAnchor}
                useTableContext={useTicketTableContext}
                openScanner={() => setShowScanner(true)}
            />
                <div className="controls-bar mobile top" />
                <div id="ticket-table-mobile-anchor" ref={mobileAnchorRef} />

            {((!filteredTableData.length || activeTableColumns.length === 0) && loading) && (
                <LoadingTableBlock type="Tickets" />
            )}

            {filteredTableData.length > 0 && activeTableColumns.length > 0 && (
             <DataTable
                sx={{ width: dataTableResponsiveWidth, m: "0 auto"}}
                data={ formatTicketTableRows(filteredTableData, activeTableColumns)}
                getKey={(row) => row.id as string} 
                labels={ activeTableLabels }
                formatCell={ formatters }
                onEdit={ canEdit ? () => {
                    setEditTicketsAnchor(tableStackRef.current)
                } : undefined }
                onDelete={ canDelete ? () => {
                    setDeleteItemsAnchor(tableStackRef.current)
                } : undefined }
                bottomWidget={
                    canCreate &&
                    !hideAddTicket && 
                    shouldShowDesktopFooter && (
                        <TableDesktopFooterMenu 
                            openModal={openTicketModal} 
                            tableContext={useTicketTableContext} 
                            buttonLabel="Add New Ticket"   
                        />
                    )
                }
                disabledRows={disabledRows}
                onSelect={selectItem}
                onDeselect={selectItem}
                selectedRows={selectedItems}
                page={page}
                perPage={perPage}
                perPageOptions={PER_PAGE_OPTIONS}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
                total={total}
                hiddenColumns={[
                    ...(hiddenColumns ?? []),
                    TICKET_KEY_ID,
                    TICKET_KEY_FOR_ASSETS
                ]}
            />
            )}


                {filteredTableData.length === 0 && !loading && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        No Tickets found.
                        {filters.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                Try adjusting filters to see more results.
                            </Box>
                        )}
                        {canCreate && !hideAddTicket && shouldShowDesktopFooter && (
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    color="success"
                                    onClick={openTicketModal}
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    sx={{
                                        borderRadius: 2,
                                    }}
                                >
                                    Add New Ticket
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

            <TableMobileMenu
                openScanner={() => setShowScanner(true)}
                setSearchAnchor={setSearchMenuAnchorMobile}
                setFilterAnchor={setFilterMenuAnchorMobile}
                openModal={canCreate ? openTicketModal : undefined}
                filters={filters}
                newLabel="New Ticket"
            />
            
            </Stack>

            <TableSearchInput
                onClose={() => setSearchMenuAnchor(null)}
                anchor={searchMenuAnchor}
                useTableDispatch={useTicketTableDispatch}
                placeholderLabel="Search Ticket IDs"
            />

            <TableFilterInput
                onClose={() => setFilterMenuAnchor(null)}
                anchor={filterMenuAnchor}
                keyOptions={ticketTableIds.filter(id => ![TICKET_KEY_ID, TICKET_KEY_FOR_ASSETS].includes(id))}
                keyLabels={activeTableLabels}
                filters={filters}
                tableColumns={tableColumns}
                tableConfig={ticketsConfig}
                addFilter={addFilter}
                removeFilter={removeFilter}
                removeAllFilters={removeAllFilters}
                filtersToRemove={filtersToRemove}
                setFiltersToRemove={setFiltersToRemove}
                getOptionsFormSelectFieldConfig={getOptionsFromSelectFieldConfig}
            >
                <ClosedTicketsFilter />
            </TableFilterInput>

            <TableSettingsMenu
                onClose={() => setSettingsAnchor(null)}
                anchor={settingsAnchor}
                keyOptions={tableColumns.map((column) => column.id)}
                keyLabels={activeTableLabels}
                addHiddenColumn={addHiddenColumn}
                removeHiddenColumn={removeHiddenColumn}
                hiddenColumns={hiddenColumns}
                removeOptions={[TICKET_KEY_ID, TICKET_KEY_FOR_ASSETS]}
            />

            <TableEditItemsPopover
                keyLabels={activeTableLabels}
                keyOptions={ticketTableIds.length ? ticketTableIds : null}
                anchor={editTicketsAnchor}
                onClose={() => setEditTicketsAnchor(null)}
                config={ticketsConfig}
                tableColumns={activeTableColumns}
                editItems={editItems}
                formatForSave={formatTicketForSave}
                itemLabel="Tickets"
                itemIds={selectedItems}
            />

            <TableDeleteItemsPopover
                anchor={deleteItemsAnchor}
                onClose={() => setDeleteItemsAnchor(null)}
                deleteItems={deleteItems}
                selectedItems={selectedItems}
                itemLabel="Ticket"
            />

            <ScannerPopover
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={async (scannedCode: string) => {
                    // Match the scanned code against the real Asset Tag (title text), falling back to Serial Number
                    const matchedAsset = await getOneAssetByAssetTag(scannedCode);
                    addFilter({
                        column: (matchedAsset ? "asset_tag" : "serial_number") as keyof WpGenericPost<WpTicketMetaBox>,
                        id: Date.now(),
                        // Asset Tag filtering only matches via Contains (LIKE) on the backend, not exact Is (=)
                        type: matchedAsset ? FilterType.Contains : FilterType.Is,
                        value: matchedAsset ? matchedAsset.title.rendered : scannedCode,
                    });
                }}
            />
        </Box>
    )
}